/* eslint-disable no-console */
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import * as Sentry from '@sentry/angular-ivy';
import { MessageService } from './message.service';

export type ErrorArgs = {
  message: string;
  error?: Error;
  delay?: number;
};

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  constructor() {
    switch (environment.logging) {
      case 'error':
        this.logging = 1;
        break;
      case 'warn':
        this.logging = 2;
        break;
      case 'info':
        this.logging = 3;
        break;
      default:
        this.logging = 4;
    }
  }

  private logCaller(level: string, ...args: unknown[]): void {
    const err = new Error(level);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(err);
      console.log(err);
    } else {
      const stack = new Error().stack;
      console.log(stack);
      err.stack = stack;
    }
    this.internalLogs.push({ date: new Date(), level: level, value: args, stack: level === 'error' ? err.stack : '' });
  }

  public debug(...args: unknown[]): void {
    if (this.logging === 4) {
      console.log(args);
      this.logCaller('debug', args);
    }
  }

  public info(...args: unknown[]): void {
    if (this.logging >= 3) {
      console.log(args);
      this.logCaller('info', args);
    }
  }

  public warn(...args: unknown[]): void {
    if (this.logging >= 2) {
      console.warn(args);
      this.logCaller('warn', args);
    }
  }

  public error({ message, delay = 0, error = undefined }: ErrorArgs): void {
    if (error) {
      this.errorSentry(error);
    }
    this.messageService.create('error', message, delay);
  }

  private errorSentry(error: Error): void {
    console.error(error);
    this.logCaller('error', error);

    Sentry.captureException(error, {
      tags: {
        online: navigator.onLine,
        version: environment.version,
      },
    });
  }

  private readonly messageService = inject(MessageService);
  private readonly logging: number;

  public readonly internalLogs: { date: Date; level: string; value: unknown; stack: string | undefined }[] = [];
}
